import { useEffect, useState } from "react";
import { endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, ShieldOff, ShieldCheck, Music2, CalendarDays } from "lucide-react";

export default function AdminModeration() {
    const [artists, setArtists] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        const [aH, aS, eH, eS] = await Promise.all([
            endpoints.adminArtists({ visibility: "hidden" }),
            endpoints.adminArtists({ visibility: "suspended" }),
            endpoints.adminEvents({ visibility: "hidden" }),
            endpoints.adminEvents({ visibility: "suspended" }),
        ]);
        setArtists([...aH.artists, ...aS.artists]);
        setEvents([...eH.events, ...eS.events]);
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const setArtistVis = async (a, vis) => {
        try { await endpoints.adminUpdateArtist(a.id, { visibility: vis }); toast.success("Updated"); load(); } catch (e) { toast.error(formatApiError(e)); }
    };
    const setEventVis = async (e, vis) => {
        try { await endpoints.adminUpdateEvent(e.id, { visibility: vis }); toast.success("Updated"); load(); } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="pm-fade" data-testid="admin-moderation">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Queue</div>
                <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Moderation</h1>
                <p className="mt-2 text-sm text-[#a3a3a3]">Hidden and suspended content. Analytics stay intact.</p>
            </header>

            <section className="px-4 md:px-8 py-6 border-b border-[#2a2a2a]">
                <div className="text-[10px] font-mono uppercase text-[#666] flex items-center gap-1 mb-3"><Music2 className="w-3 h-3" /> Artists</div>
                {loading ? <div className="text-xs font-mono text-[#666]">Loading…</div>
                : artists.length === 0 ? <div className="text-xs font-mono text-[#666]" data-testid="mod-artists-empty">Nothing hidden or suspended.</div>
                : (
                    <div className="border border-[#2a2a2a]">
                        {artists.map((a) => (
                            <div key={a.id} className="grid grid-cols-12 gap-3 items-center p-3 border-b border-[#2a2a2a] last:border-b-0" data-testid={`mod-artist-${a.id}`}>
                                <div className="col-span-12 md:col-span-6">
                                    <div className="font-display font-bold uppercase text-sm truncate">{a.name}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#666] truncate">{a.genre} · {a.city}, {a.country}</div>
                                </div>
                                <div className="col-span-4 md:col-span-2 text-[10px] font-mono uppercase text-[#a3a3a3]">{a.visibility}</div>
                                <div className="col-span-8 md:col-span-4 flex gap-1 justify-end">
                                    <button onClick={() => setArtistVis(a, "public")} className="pm-btn text-[10px]" data-testid={`mod-artist-restore-${a.id}`}><Eye className="w-3 h-3 mr-1" /> Restore</button>
                                    {a.visibility !== "suspended" && <button onClick={() => setArtistVis(a, "suspended")} className="pm-btn text-[10px]" data-testid={`mod-artist-suspend-${a.id}`}><ShieldOff className="w-3 h-3 mr-1" /> Suspend</button>}
                                    {a.visibility !== "hidden" && <button onClick={() => setArtistVis(a, "hidden")} className="pm-btn text-[10px]" data-testid={`mod-artist-hide-${a.id}`}><EyeOff className="w-3 h-3 mr-1" /> Hide</button>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <section className="px-4 md:px-8 py-6">
                <div className="text-[10px] font-mono uppercase text-[#666] flex items-center gap-1 mb-3"><CalendarDays className="w-3 h-3" /> Events</div>
                {loading ? <div className="text-xs font-mono text-[#666]">Loading…</div>
                : events.length === 0 ? <div className="text-xs font-mono text-[#666]" data-testid="mod-events-empty">Nothing hidden or suspended.</div>
                : (
                    <div className="border border-[#2a2a2a]">
                        {events.map((e) => (
                            <div key={e.id} className="grid grid-cols-12 gap-3 items-center p-3 border-b border-[#2a2a2a] last:border-b-0" data-testid={`mod-event-${e.id}`}>
                                <div className="col-span-12 md:col-span-6">
                                    <div className="font-display font-bold uppercase text-sm truncate">{e.name}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#666] truncate">{e.artist_name} · {e.venue_name} · {e.date}</div>
                                </div>
                                <div className="col-span-4 md:col-span-2 text-[10px] font-mono uppercase text-[#a3a3a3]">{e.visibility}</div>
                                <div className="col-span-8 md:col-span-4 flex gap-1 justify-end">
                                    <button onClick={() => setEventVis(e, "public")} className="pm-btn text-[10px]" data-testid={`mod-event-restore-${e.id}`}><ShieldCheck className="w-3 h-3 mr-1" /> Restore</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
